import {DateTime} from 'luxon';
import {rrulestr, RRuleStrOptions} from 'rrule';

import {isInteger} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';

export function toEpochMillisFromRRuleLibraryDate(jsDate: Date, optionalTimezone: string | null | undefined) {
  return DateTime.fromJSDate(jsDate)
    .toUTC()
    .setZone(optionalTimezone || 'default', {keepLocalTime: true})
    .valueOf();
}

/*
 * RRule parsing.
 */

interface ParseRecurrenceRuleOptions {
  timezone?: string | null;
  startsAt?: number; // note: this is in MS.
}

export function parseRecurrenceRuleFromString(
  recurrenceString: string,
  {timezone, startsAt}: ParseRecurrenceRuleOptions = {},
) {
  const ruleOptions: Partial<RRuleStrOptions> = {
    // Given `rrule`'s implementation, passing `null` means we want to _NOT_ consider any timezone whatsoever, while passing `undefined` means we should instead consider the timezone of `dstart`.
    // You would think that not passing `tzid` entirely would be equivalent to `tzid: undefined` but due to `rrule`'s option handling, `tzid` will be coerced to `tzid: null`.
    tzid: timezone === null ? timezone : undefined,
    dtstart: isInteger(startsAt) ? toRRuleLibraryDateFromEpochMillis(startsAt, timezone) : undefined,
    forceset: true,
  };

  // Generate all the instance start times within the current range.
  // RRule only supports UTC times so we force the timezone conversion
  // whenever providing inputs to or parsing outputs from the library.
  // More info: https://github.com/jakubroztocil/rrule#important-use-utc-dates
  return rrulestr(recurrenceString, ruleOptions);
}

export function getInstanceTimesInRange(
  recurrenceString: string,
  startsAt: number,
  optionalTimezone: string | null | undefined,
  rangeStartsAt: number,
  rangeEndsAt: number,
) {
  const ruleSet = parseRecurrenceRuleFromString(recurrenceString, {
    timezone: optionalTimezone,
    startsAt,
  });
  return ruleSet
    .between(DateTime.fromMillis(rangeStartsAt).toJSDate(), DateTime.fromMillis(rangeEndsAt).toJSDate())
    .map((d) => toEpochMillisFromRRuleLibraryDate(d, optionalTimezone));
}

function toRRuleLibraryDateFromEpochMillis(epochMillis: number, optionalTimezone: string | null | undefined) {
  return DateTime.fromMillis(epochMillis)
    .setZone(optionalTimezone || 'default')
    .setZone('utc', {keepLocalTime: true})
    .toJSDate();
}
